<template><div><h2 id="layout-stops" tabindex="-1" class="d-docsite--header-2">Layout stops <a class="header-anchor d-link d-docsite--link d-link" href="#layout-stops" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-hmx-{stop}</code> to set a fixed maximum height for an element using layout token stops. The hyphen before the number indicates a layout token reference, e.g. <code v-pre>d-hmx-100</code> outputs <code v-pre>max-block-size: var(--dt-layout-100)</code> (64px). This can be combined with <code v-pre>d-h{n}p</code> and <code v-pre>d-hmn-{stop}</code> to have an element fill a certain height range.</p>
<dt-notice kind="info" title="Scale-indexed vs pixel-indexed stops" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
Bare integer stops (<code v-pre>25</code>, <code v-pre>50</code>, <code v-pre>100</code>, …) are scale-indexed on the 64px base — <code v-pre>value_in_px = stop × 64 / 100</code>, so <code v-pre>25</code> = 16px and <code v-pre>100</code> = 64px. Stops with a <code v-pre>px</code> suffix (<code v-pre>1px</code>, <code v-pre>2px</code>, <code v-pre>8px</code>, <code v-pre>20px</code>, <code v-pre>24px</code>) are off-scale exceptions that encode the literal pixel value.
</dt-notice>
<code-example source-code='&lt;div class=&quot;d-hmx-100&quot;&gt;...&lt;/div&gt;  &lt;!-- max-block-size: var(--dt-layout-100) = 64px / 6.4rem --&gt;
&lt;div class=&quot;d-hmx-200&quot;&gt;...&lt;/div&gt;  &lt;!-- max-block-size: var(--dt-layout-200) = 128px / 12.8rem --&gt;
&lt;div class=&quot;d-hmx-400&quot;&gt;...&lt;/div&gt;  &lt;!-- max-block-size: var(--dt-layout-400) = 256px / 25.6rem --&gt;'>
<dt-stack direction="row" gap="200" align="start" justify="center" class="d-w100p">
  <dt-stack direction="row" align="center" justify="center" class="d-py-200 d-px-100 d-w100p d-h-100 d-hmx-100 d-bgc-moderate d-bar-300 d-ta-center">1</dt-stack>
  <dt-stack direction="row" align="center" justify="center" class="d-py-200 d-px-100 d-w100p d-h-200 d-hmx-200 d-bgc-moderate d-bar-300 d-ta-center">2</dt-stack>
  <dt-stack direction="row" align="center" justify="center" class="d-py-200 d-px-100 d-w100p d-h-400 d-hmx-400 d-bgc-moderate d-bar-300 d-ta-center">3</dt-stack>
</dt-stack>
</code-example>
<h2 id="percentages" tabindex="-1" class="d-docsite--header-2">Percentages <a class="header-anchor d-link d-docsite--link d-link" href="#percentages" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-hmx{n}p</code> to set a maximum height percentage for an element. No hyphen before the number, <code v-pre>p</code> suffix indicates a literal percentage value. Note: <code v-pre>d-hmx33p</code> = 33.333% and <code v-pre>d-hmx66p</code> = 66.667%.</p>
<code-example only-show="code" source-code='&lt;div class=&quot;d-h-350&quot;&gt;
  &lt;div class=&quot;d-h-100 d-hmx50p&quot;&gt;1&lt;/div&gt;
&lt;/div&gt;'>
<div class="d-h-350">
  <div class="d-h-100 d-hmx50p">1</div>
</div>
</code-example>
<h2 id="keywords" tabindex="-1" class="d-docsite--header-2">Keywords <a class="header-anchor d-link d-docsite--link d-link" href="#keywords" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use keyword utilities to set maximum height using CSS keyword values.</p>
<code-example only-show="code" source-code='&lt;div class=&quot;d-hmx-auto&quot;&gt;...&lt;/div&gt;
&lt;div class=&quot;d-hmx-unset&quot;&gt;...&lt;/div&gt;
&lt;div class=&quot;d-hmx-fit-content&quot;&gt;...&lt;/div&gt;
&lt;div class=&quot;d-hmx-max-content&quot;&gt;...&lt;/div&gt;
&lt;div class=&quot;d-hmx-min-content&quot;&gt;...&lt;/div&gt;'>
<div class="d-hmx-auto">...</div>
<div class="d-hmx-unset">...</div>
<div class="d-hmx-fit-content">...</div>
<div class="d-hmx-max-content">...</div>
<div class="d-hmx-min-content">...</div>
</code-example>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<clamped-table-wrapper>
  <div>
    <table class="d-table dialtone-doc-table">
      <thead class="d-bgc-primary d-ps-sticky d-zi-base1 d-ibs-0">
        <tr>
          <th scope="col" class="d-p-0 d-bbw0 d-w30p"><div class="d-p-200 d-bb d-bbw1">Class</div></th>
          <th scope="col" class="d-p-0 d-bbw0"><div class="d-p-200 d-bb d-bbw1">Output</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="i in layout">
          <th scope="row" class="d-code--sm d-docsite-code">.d-hmx-{{ i.stop }}</th>
          <td class="d-code--sm">max-block-size: var(--dt-layout-{{ i.stop }}) !important; <span class="d-fc-tertiary">/* {{ i.px }}px / {{ (i.px / 10).toFixed(1) }}rem */</span></td>
        </tr>
      </tbody>
      <tbody>
        <tr v-for="i in percentage">
          <th scope="row" class="d-code--sm d-docsite-code">.d-hmx{{ i }}p</th>
          <td class="d-code--sm">max-block-size: {{ i === 33 ? '33.333' : i === 66 ? '66.667' : i }}% !important;</td>
        </tr>
      </tbody>
      <tbody>
        <tr v-for="i in keywords">
          <th scope="row" class="d-code--sm d-docsite-code">.d-hmx-{{ i }}</th>
          <td class="d-code--sm">max-block-size: {{ i }} !important;</td>
        </tr>
      </tbody>
    </table>
  </div>
</clamped-table-wrapper>
</div></template>


<script setup>
  import { percentage, layout, keywords } from '@data/width-height.json';
  import ClampedTableWrapper from '@baseComponents/ClampedTableWrapper.vue';
</script>